import { useEffect, useRef, useCallback, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import type { TioTileField } from '../../api/tomorrowIoTilesService';
import type { RadarTile } from './useDataFetching';
import type { EventWithCircuit } from '../../api/eventsService';
import type { CircuitWithGeoJSON } from '../../api/circuitsService';
import type { StationData } from '../../api/stationObservationsService';
import { saveSplitMapViewForEvent, getEventKey, resolveSplitMapViewForEvent } from './nowcastingStorage';
import type { MapTileProvider } from './useNowcastingState';
import { resolveMapStyle } from './nowcastingMapIds';
import { useMapLayers } from './useMapLayers';

interface UseSplitMapParams {
  selectedEvent: EventWithCircuit | null;
  isSplitViewEnabled: boolean;
  splitPosition: number;
  mapStyle: string | null;
  mapStyles: Record<string, { name: string; url: string }>;
  theme: string;
  circuitData: CircuitWithGeoJSON | null;
  showWindArrows: boolean;
  stations: StationData[];
  tiles: RadarTile[];
  currentIndex: number;
  extendedTiles: RadarTile[];
  mapTileProvider: MapTileProvider;
  tioTimestamps: string[];
  tioCurrentIndex: number;
  tioTileField: TioTileField;
  radarOpacity: number;
  fps: number;
  tilesSource: string;
  // Circle overlay settings
  isCircleOverlayVisible: boolean;
  showRadarBbox: boolean;
  showRadiusCircles: boolean;
  radiusCircleCount: number;
  radiusCircleDistance: number;
  // Track line width
  trackLineWidth?: number;
  // Refs
  splitMapContainerRef: React.RefObject<HTMLDivElement | null>;
  splitMapRef: React.RefObject<mapboxgl.Map | null>;
  numericBlobUrl?: string | null;
}

interface UseSplitMapReturn {
  handleCenterOnCircuit: () => void;
  handleResetMapView: () => void;
  splitStyleVersion: number;
}

/**
 * Custom hook to manage the split view secondary map (left map).
 * Handles map initialization, circuit/station rendering, radar layers, TIO layers,
 * wind arrows, and circle overlays - mirroring the main map but with independent position/zoom.
 */
export function useSplitMap({
  selectedEvent,
  isSplitViewEnabled,
  splitPosition,
  mapStyle,
  mapStyles,
  theme,
  circuitData,
  showWindArrows,
  stations,
  tiles,
  currentIndex,
  extendedTiles,
  mapTileProvider,
  tioTimestamps,
  tioCurrentIndex,
  tioTileField,
  radarOpacity,
  fps,
  tilesSource,
  isCircleOverlayVisible,
  showRadarBbox,
  showRadiusCircles,
  radiusCircleCount,
  radiusCircleDistance,
  trackLineWidth = 3,
  splitMapContainerRef,
  splitMapRef,
  numericBlobUrl,
}: UseSplitMapParams): UseSplitMapReturn {
  // Style version - incremented on map load to trigger overlay effects to re-run
  // This mirrors how the main map uses styleVersion
  const [splitStyleVersion, setSplitStyleVersion] = useState(0);

  // TIO active layer tracking (passed to useMapLayers)
  const splitTioActiveLayerRef = useRef<'layer1' | 'layer2'>('layer1');
  const selectedEventRef = useRef(selectedEvent);
  selectedEventRef.current = selectedEvent;
  const previousEventKeyRef = useRef<string | null>(null);
  const splitViewReady = selectedEvent != null && isSplitViewEnabled;

  // Handler for centering on circuit
  const handleCenterOnCircuit = useCallback(() => {
    if (splitMapRef.current && selectedEvent) {
      splitMapRef.current.flyTo({
        center: [selectedEvent.circuit.longitude || 0, selectedEvent.circuit.latitude || 0],
      });
    }
  }, [splitMapRef, selectedEvent]);

  // Handler for resetting map view
  const handleResetMapView = useCallback(() => {
    if (splitMapRef.current && selectedEvent) {
      splitMapRef.current.flyTo({
        center: [selectedEvent.circuit.longitude || 0, selectedEvent.circuit.latitude || 0],
        zoom: 13,
        bearing: 0,
        pitch: 0,
      });
    }
  }, [splitMapRef, selectedEvent]);

  // Create or recreate split map when style/theme/visibility changes (not on event switch).
  useEffect(() => {
    if (!splitMapContainerRef.current || !selectedEvent || !isSplitViewEnabled) return;

    const mapContainer = splitMapContainerRef.current;
    const { center: initialCenter, zoom: initialZoom } = resolveSplitMapViewForEvent(selectedEvent);
    const styleUrl = resolveMapStyle(mapStyle, mapStyles, theme);

    const splitMap = new mapboxgl.Map({
      container: splitMapContainerRef.current,
      style: styleUrl,
      center: initialCenter,
      zoom: initialZoom,
      minZoom: 6,
      interactive: true,
      projection: 'mercator',
      preserveDrawingBuffer: true,
    });

    splitMap.addControl(new mapboxgl.NavigationControl(), 'top-right');

    const onMoveEnd = () => {
      const event = selectedEventRef.current;
      if (!event) return;
      saveSplitMapViewForEvent(event, splitMap.getCenter(), splitMap.getZoom());
    };
    splitMap.on('moveend', onMoveEnd);

    const zoomIndicator = document.createElement('div');
    zoomIndicator.setAttribute('data-split-zoom-indicator', 'true');
    zoomIndicator.className = 'mapboxgl-ctrl';
    zoomIndicator.style.cssText = `
      position: absolute;
      bottom: 20px;
      left: 20px;
      background: ${theme === 'dark' ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.9)'};
      color: ${theme === 'dark' ? '#fff' : '#000'};
      padding: 6px 12px;
      border-radius: 4px;
      font-size: 12px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
      z-index: 1;
      pointer-events: none;
      user-select: none;
    `;
    zoomIndicator.textContent = `Zoom: ${splitMap.getZoom().toFixed(1)}`;
    mapContainer.appendChild(zoomIndicator);

    const updateZoomIndicator = () => {
      zoomIndicator.textContent = `Zoom: ${splitMap.getZoom().toFixed(1)}`;
    };
    splitMap.on('zoom', updateZoomIndicator);
    splitMap.on('zoomend', updateZoomIndicator);

    splitMap.on('load', () => {
      splitMap.resize();
      setSplitStyleVersion(v => v + 1);

      setTimeout(() => {
        const attributionButton = mapContainer?.querySelector('.mapboxgl-ctrl-attrib-button') as HTMLElement;
        if (attributionButton) {
          attributionButton.click();
        }
      }, 100);
    });

    splitMapRef.current = splitMap;
    previousEventKeyRef.current = getEventKey(selectedEvent);

    return () => {
      splitMap.off('moveend', onMoveEnd);
      splitMap.off('zoom', updateZoomIndicator);
      splitMap.off('zoomend', updateZoomIndicator);
      mapContainer?.querySelector('[data-split-zoom-indicator="true"]')?.remove();
      splitMap.remove();
      splitMapRef.current = null;
      previousEventKeyRef.current = null;
    };
  }, [splitViewReady, mapStyle, mapStyles, theme, isSplitViewEnabled, splitMapContainerRef, splitMapRef]);

  // Reuse split map when switching events.
  useEffect(() => {
    const splitMap = splitMapRef.current;
    if (!splitMap || !selectedEvent || !isSplitViewEnabled) return;

    const eventKey = getEventKey(selectedEvent);
    if (previousEventKeyRef.current === eventKey) return;

    previousEventKeyRef.current = eventKey;
    const { center, zoom } = resolveSplitMapViewForEvent(selectedEvent);
    splitMap.jumpTo({ center, zoom });
  }, [selectedEvent, isSplitViewEnabled, splitMapRef]);

  // Resize split map when split position changes
  useEffect(() => {
    if (splitMapRef.current && isSplitViewEnabled) {
      setTimeout(() => {
        splitMapRef.current?.resize();
      }, 0);
    }
  }, [splitPosition, isSplitViewEnabled, splitMapRef]);

  // All layers via composite hook
  useMapLayers({
    mapRef: splitMapRef,
    role: 'split',
    enabled: isSplitViewEnabled,
    styleVersion: splitStyleVersion,
    selectedEvent,
    circuitData,
    trackLineWidth,
    showWindArrows,
    stations,
    tiles,
    currentIndex,
    isCircleOverlayVisible,
    showRadarBbox,
    showRadiusCircles,
    radiusCircleCount,
    radiusCircleDistance,
    extendedTiles,
    radarOpacity,
    fps,
    tilesSource,
    mapTileProvider,
    numericBlobUrl,
    tioTimestamps,
    tioCurrentIndex,
    tioTileField,
    tioActiveLayerRef: splitTioActiveLayerRef,
  });

  return {
    handleCenterOnCircuit,
    handleResetMapView,
    splitStyleVersion,
  };
}
